import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useProfileMetadata } from '../../../hooks/profile/useProfileMetadata'
import { useGetPicture } from '../../../hooks/profile/useGetPicture'

import Loading from '../../../components/loading/Loading'
import { Button } from '../../../components/buttons/Button'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import { UserCard } from '../../../components/authenticated/management/user/userCard/UserCard'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { ProfileMetadata } from '../../../types/user/userProfile'
import { getUserFullName } from '../../../utils/users/user'

const USERS_PATH = '/a/users'

export const AdminUser = () => {
  const navigate = useNavigate()
  const { profileId } = useParams()
  const { showBanner } = useApp()
  const { t } = useTranslation('users')
  const [userDetails, setUserDetails] = useState<ProfileMetadata | null>(null)
  const [isResolving, setIsResolving] = useState(true)
  const [picture, setPicture] = useState<Blob | null>(null)
  const [pictureUrl, setPictureUrl] = useState<string | null>(null)
  const { getPicture } = useGetPicture()

  const { profileMetadata, loading, error, resetState } = useProfileMetadata()

  useEffect(() => {
    const loadData = async () => {
      setUserDetails(null)
      setIsResolving(true)

      if (!profileId?.trim()) {
        navigate(USERS_PATH, { replace: true })
        return
      }

      setPicture(null)
      const response = await profileMetadata(profileId)

      if (response) {
        setUserDetails(response)
        // The picture is optional; the card renders with initials until (or unless) it loads.
        void getPicture(response.id).then(setPicture)
        resetState()
        setIsResolving(false)
        return
      }

      navigate(USERS_PATH, { replace: true })
    }

    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId])

  useEffect(() => {
    if (!error) {
      return
    }

    showBanner({ kind: BannerKind.ERR, msg: error })
    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (!picture) {
      setPictureUrl(null)
      return
    }

    const url = URL.createObjectURL(picture)
    setPictureUrl(url)

    return () => URL.revokeObjectURL(url)
  }, [picture])

  const fullName = userDetails ? getUserFullName(userDetails) : ''

  const handleBack = useCallback(() => {
    navigate(USERS_PATH)
  }, [navigate])

  return (
    <>
      <PageHeader
        title={{
          eyebrow: t('users.view.eyebrow'),
          h1: fullName || t('users.view.title'),
        }}
        sub={t('users.view.subtitle')}
        actions={[
          <Button
            key="back"
            label={t('users.view.page-header.back-btn')}
            size="md"
            onClick={handleBack}
          />,
        ]}
      />

      {isResolving || loading ? (
        <Loading variant="card" />
      ) : (
        userDetails && (
          <UserCard
            user={userDetails}
            picture={pictureUrl}
            layout="page"
            roles={
              (userDetails.roleTypeList ?? []).length === 0 ? (
                <span className="mono">{t('users.list.unavailable')}</span>
              ) : (
                <>
                  {userDetails.roleTypeList.map(role => (
                    <span key={role} className="badge">
                      {t(`users.roles.${role.toLowerCase()}`)}
                    </span>
                  ))}
                </>
              )
            }
          />
        )
      )}
    </>
  )
}
